import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export const useClients = (filters = {}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [JSON.stringify(filters)]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('clients')
        .select(`
          *,
          rfqs(id, status)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,abn.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate RFQ counts for each client
      const clientsWithCounts = (data || []).map(client => {
        const rfqs = client.rfqs || [];
        const invitedStatuses = ['invited', 'in_progress', 'submitted', 'pending_outcome', 'successful', 'unsuccessful'];

        return {
          ...client,
          rfq_count: rfqs.length,
          invited_rfqs_count: rfqs.filter(rfq =>
            invitedStatuses.includes(rfq.status?.toLowerCase())
          ).length
        };
      });

      setClients(clientsWithCounts);
      setError(null);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { clients, loading, error, refetch: fetchClients };
};

export const useClient = (clientId) => {
  const [client, setClient] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);

      // Fetch client info
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (clientError) throw clientError;

      // Fetch RFQs for this client
      const { data: rfqsData, error: rfqsError } = await supabase
        .from('rfqs')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (rfqsError) throw rfqsError;

      // Fetch client contacts (if table exists)
      let contactsData = [];
      try {
        const { data, error: contactsError } = await supabase
          .from('client_contacts')
          .select('*')
          .eq('client_id', clientId)
          .order('priority', { ascending: false });

        if (!contactsError) {
          contactsData = data || [];
        }
      } catch (contactsErr) {
        // Table might not exist yet, ignore error
        console.log('Client contacts table not available yet');
      }

      setClient(clientData);
      setRfqs(rfqsData || []);
      setContacts(contactsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching client details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { client, rfqs, contacts, loading, error, refetch: fetchClientDetails };
};
